import type { AssetGroup, GetAssetType, Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { setAssetIntroExitTimesOperation } from './asset-timing-operations';

import type { BackgroundRemovalResponse, ImageAssetModel, VideoAssetModel } from 'js/src/client';
import { ApiService, SourceEnum, TaskResultMessageTypeEnum } from 'js/src/client';
import { getVideoId } from 'js/src/data/app';
import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates';
import {
    getEditorFromSceneGroupId,
    getTranscriptCurrentTime,
} from 'js/src/data/transcript/transcript-selectors';
import { $getFineOverlayBoundaryTimeCloseTo } from 'js/src/data/transcript/transcript-utils';
import { checkpointVideo } from 'js/src/data/video-data';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import type { LuminaryOperationContext, OperationCall } from 'js/src/luminary/luminary-hooks';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    createOverlayOperation,
    setAssetPositionTransformationOperation,
    setUpMediaOnlyOverlayOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getAdvancedMediaControlsAsset,
    getSceneGroupCategory,
    getSceneWithBaseMediaAsset,
    getVoiceDrivenAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isSceneAnOverlay,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceDrivenSceneUsingPlaceholderBaseAsset,
    isVoiceoverSceneGroup,
    translateSceneGroupTimeToTranscriptTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import type { ConfigurableElementProperties } from 'js/src/luminary/luminary-types';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { getVoicedrivenSceneStartTime } from 'js/src/luminary/voicedriven-scenes';

/**
 * Fetches the specified asset from a scene and functionally "empties" its source data so
 * it doesn't display anything.
 */
export const clearMediaAssetSourceDataOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        assetId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        const mediaAsset = scene.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
        if (!mediaAsset)
            throw new Error('clearMediaAssetSourceDataOperation: asset not found in scene');
        clearSourceData(mediaAsset);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Media asset cleared', { sceneId });
    },
);

/**
 * Functionally "empties" out the media asset so it doesn't display anything
 */
export function clearSourceData(mediaAsset: TimeAwareMediaAsset) {
    mediaAsset.sourceData = INVISIBLE_IMAGE_SOURCE_DATA;
    // clear the attribution (no media!)
    mediaAsset.slide!.attribution = null;
}

/**
 * Given a sceneGroupId, updates the volume of the base asset for that scene group
 * (for talking head or voiceover).
 */
export const setVoicedrivenBaseAssetVolumeOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        sceneGroupId: string,
        volume: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        if (isTalkingHeadSceneGroup(sceneGroup)) {
            sceneGroup.mediaAsset.volume = volume;
        } else if (isVoiceoverSceneGroup(sceneGroup)) {
            sceneGroup.voiceoverAsset.volume = volume;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('voicedriven base asset volume changed', {
            new_volume: volume,
            sceneGroupId,
            sceneCategory: getSceneGroupCategory(sceneGroup),
        });
    },
);
/**
 * Given the asset name of a media asset with a video source, updates its volume
 * for all scenes in the group
 */
export const setVideoAssetVolumeForGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        sceneGroupId: string,
        assetId: string,
        volume: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const mediaAsset = sceneGroup.getAssetById(assetId) as TimeAwareMediaAsset | undefined;
        if (!mediaAsset)
            throw new Error('setVideoAssetVolumeForGroupOperation: asset not found in scene group');
        if (mediaAsset.linkToSceneGroup) {
            sceneGroup.mediaAsset.volume = volume;
        } else {
            mediaAsset.volume = volume;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('video asset volume changed', {
            new_volume: volume,
            sceneId: mediaAsset.slide?.id,
            sceneGroupId,
            sceneCategory: mediaAsset.slide?.sceneCategory,
        });
    },
);

/**
 * Scale crop and zoom values based on the target resolution of a media asset.
 * This helps so the media don't look distorted when copied between targets of different resolutions.
 */
export function adjustAssetCropByResolution(
    targetAsset: TimeAwareMediaAsset,
    originalAsset: TimeAwareMediaAsset,
    adjustZoom = true,
) {
    if (!targetAsset || !originalAsset) {
        return;
    }
    const allowedDelta = 5; // Allow 5 px width/height discrepancy when deducing equality
    const targetResolution =
        targetAsset.mediaTargetResolution.width / targetAsset.mediaTargetResolution.height;
    const originalResolution =
        originalAsset.mediaTargetResolution.width / originalAsset.mediaTargetResolution.height;
    const isEqualResolution =
        Math.abs(targetResolution * 100 - originalResolution * 100) < allowedDelta;

    const widthRatio =
        originalAsset.mediaTargetResolution.width / targetAsset.mediaTargetResolution.width;
    const heightRatio =
        originalAsset.mediaTargetResolution.height / targetAsset.mediaTargetResolution.height;
    // If the resolution of the media target is the same with the base scene, we can apply the same crop
    // to the new scene since the media will look the same and this is likely what the user expects.
    // Otherwise, we adjust the crop based on the asset resolution ratio and the given scale factor.
    let offsetX = originalAsset.xTranslation;
    let offsetY = originalAsset.yTranslation;
    const scale = originalAsset.scaleX;
    if (!isEqualResolution) {
        offsetX = originalAsset.xTranslation * widthRatio;
        offsetY = originalAsset.yTranslation * heightRatio;
    }
    targetAsset.xTranslation = offsetX;
    targetAsset.yTranslation = offsetY;
    if (adjustZoom) {
        targetAsset.scaleX = scale;
        targetAsset.scaleY = scale;
    }
}

/**
 * Removes the background from a media asset (image or video).
 */
export const replaceWithRemovedBackgroundOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call }: LuminaryOperationContext<Luminary>,
        targetAssetId: string,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!targetAssetId || !sceneId) {
            throw new Error('Insufficient data to remove background from media');
        }
        const { mediaAsset } = getAdvancedMediaControlsAsset(luminary, sceneId, targetAssetId);
        if (!mediaAsset) {
            throw new Error('Asset not found for background removal');
        }
        let newAssetModel: ImageAssetModel | VideoAssetModel | undefined;
        let created = false; // Whether a new asset was created (due to a new background removal generation)
        let backgroundAlreadyRemoved = false; // Whether the background was already removed for this asset
        const videoId = getVideoId(getState());
        if (!videoId) {
            throw new Error('No video id found');
        }
        try {
            let asset_uuid, asset_data;
            // DAM assets are not stored in the database. We supply the asset data so that the asset is saved to the database before the background removal is performed
            if (mediaAsset.sourceData.source === SourceEnum.SOURCE_DAM) {
                if (!mediaAsset.sourceData.render_url) {
                    throw new Error('Render url not found for DAM asset');
                }
                asset_data = {
                    preview_url: mediaAsset.sourceData.preview_url,
                    thumbnail_url: mediaAsset.sourceData.thumbnail_url,
                    render_url: mediaAsset.sourceData.render_url,
                    width: mediaAsset.sourceData.width,
                    height: mediaAsset.sourceData.height,
                    source: mediaAsset.sourceData.source,
                    source_id: mediaAsset.sourceData.source_id,
                    license: mediaAsset.sourceData.license,
                    title: mediaAsset.sourceData.title,
                    tags: [],
                };
            } else {
                asset_uuid = mediaAsset.sourceData.uuid;
            }
            const taskResultResponse = await ApiService.backgroundRemovalCreate({
                body: {
                    asset_uuid,
                    asset_data,
                    video_id: videoId,
                },
            });
            const ws = WebSocketClient.getInstance();
            const response = await ws.waitForMessage<BackgroundRemovalResponse>(
                taskResultResponse.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'remove_background_from_media',
            );
            newAssetModel = response?.asset?.image ?? undefined;
            created = response?.created ?? false;
            if (!newAssetModel) {
                throw new Error('No asset returned from background removal');
            }
        } catch (error) {
            ErrorReporter.capture(error);
            throw error;
        }

        if (newAssetModel) {
            const asset = luminary.getAssetById(targetAssetId) as TimeAwareMediaAsset | undefined;
            if (!asset) {
                throw new Error('Asset not found');
            }
            const original_url = asset.sourceData.render_url ?? asset.sourceData.preview_url;
            // If the asset has the same url as the returned asset, then the background was already removed
            if (asset.sourceData.preview_url === newAssetModel.preview_url) {
                backgroundAlreadyRemoved = true;
                analytics.track('Background already removed for media asset');
            }

            asset.setSourceDataWithoutResettingProperties({
                ...asset.sourceData,
                uuid: newAssetModel.uuid || asset.sourceData.uuid,
                source: newAssetModel.source,
                source_id: newAssetModel.source_id,
                thumbnail_url: newAssetModel.thumbnail_url,
                preview_url: newAssetModel.preview_url,
                render_url: newAssetModel.render_url,
                width: newAssetModel.width,
                height: newAssetModel.height,
            });
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            if (!backgroundAlreadyRemoved) {
                analytics.track('Background removed from media asset', {
                    original_url,
                    url: newAssetModel.preview_url,
                    isNewGeneration: created,
                });
            }

            return backgroundAlreadyRemoved;
        }
        // This should never happen
        throw new Error('No asset returned from background removal');
    },
);

/**
 * A scene is compatible with an asset if it is:
 * - a text on media scene
 * - an overlay
 *
 * If it is not compatible, create an overlay
 */
export const getOrCreateStickerCompatibleSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        targetSceneId,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): Scene | null => {
        const TARGET_OVERLAY_DURATION = 3000;

        const targetScene = luminary.getSceneById(targetSceneId);
        if (!targetScene) {
            return null;
        }
        const sceneGroup = targetScene.sceneGroup;
        if (!sceneGroup) {
            return null;
        }

        if (!isVoiceDrivenSceneGroup(sceneGroup) || isSceneAnOverlay(getState(), targetScene)) {
            return targetScene;
        }

        if (isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup)) {
            return null;
        }

        // Target scene is not compatible, create an overlay scene
        const templateId = luminary.template.id;
        const defaultCutawaySceneDesign = getTemplateDefaultCutawaySceneDesign(
            getState(),
            templateId,
        );
        if (!defaultCutawaySceneDesign) {
            throw new Error(
                'getOrCreateStickerCompatibleSceneOperation missing defaultCutawaySceneDesign',
            );
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            throw new Error('getOrCreateStickerCompatibleSceneOperation missing editor');
        }

        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new Error('getOrCreateStickerCompatibleSceneOperation missing voicedriven asset');
        }

        const sceneIndex = targetScene.localIndex;
        const leftScene = sceneGroup.scenes[sceneIndex - 1];
        const rightScene = sceneGroup.scenes[sceneIndex + 1];
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        const translateToTranscriptTime = (time: number, { includeTrailingCut = true } = {}) => {
            return translateVideoTimeToTranscriptTime(
                time,
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenBaseAssetSourceDuration,
                sceneGroup.startTime,
                { includeTrailingCut },
            );
        };

        let newScene: Scene | undefined;
        try {
            const transcriptTime = getTranscriptCurrentTime(getState());
            let targetStartTime = voiceDrivenAsset.startTime;
            if (transcriptTime.sceneId === targetScene.id) {
                targetStartTime = translateSceneGroupTimeToTranscriptTime(
                    transcriptTime.currentTimeMillis,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                );
            } else if (leftScene) {
                targetStartTime = translateToTranscriptTime(
                    getVoicedrivenSceneStartTime(targetScene),
                );
            }

            const targetEndTime = Math.min(
                rightScene
                    ? translateToTranscriptTime(getVoicedrivenSceneStartTime(rightScene))
                    : Infinity,
                targetStartTime + TARGET_OVERLAY_DURATION,
                translateToTranscriptTime(sceneGroup.endTime),
            );

            const { startTime, endTime } = editor.getEditorState().read(() => {
                const startTime = $getFineOverlayBoundaryTimeCloseTo(
                    targetStartTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                    { getIdealOverlayBoundaryTime: true },
                );
                const endTime = $getFineOverlayBoundaryTimeCloseTo(
                    targetEndTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenBaseAssetSourceDuration,
                    { getIdealOverlayBoundaryTime: true },
                );
                return {
                    startTime,
                    endTime,
                };
            });

            const newSceneId = call(
                createOverlayOperation,
                sceneGroup.id,
                startTime,
                endTime,
                defaultCutawaySceneDesign,
                systemTriggered,
                parentActionGroup,
            );
            newScene = luminary.getSceneById(newSceneId);
            if (!newScene) {
                throw Error('Unable to find overlay scene');
            }
        } catch (e) {
            ErrorReporter.capture(e);
            return null;
        }

        // Set up scene: set the cut away media to be the base asset and clear any text
        try {
            // Voiceover scene groups (without an AI avatar) have no base media asset to link
            // the overlay's media slot to, so skip the talking-head media setup in that case.
            const hasBaseMediaAsset =
                !isVoiceoverSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup);
            call(
                setUpMediaOnlyOverlayOperation,
                newScene.id,
                hasBaseMediaAsset ? getSceneWithBaseMediaAsset(sceneGroup) : undefined,
            );
        } catch (e) {
            ErrorReporter.capture(e);
        }

        return newScene;
    },
);

/**
 * Adds a blank sticker to a scene. It DOES NOT set the source data so avoid calling this directly
 * Call setIconAssetForSceneOperation or setMediaAssetForSceneOperation
 */
function addStickerAssetToSceneInternal<
    T extends (typeof VideoData.ALL_STICKER_ASSET_TYPES)[number],
>(
    {
        luminary,
        call,
    }: {
        luminary: Luminary;
        call: OperationCall;
    },
    container: Scene | AssetGroup,
    assetType: T,
    propertyOverrides?: Partial<ConfigurableElementProperties>,
    systemTriggered = false,
    parentActionGroup = uuid(),
): GetAssetType<T> | null {
    const asset = container.addSticker(assetType);
    const scene = asset.slide!;

    call(
        configureStickerAssetOperation,
        asset.id,
        scene.id,
        propertyOverrides,
        systemTriggered,
        parentActionGroup,
    );

    analytics.track('Sticker added to scene', {
        type: assetType,
        sceneGroupCategory: getSceneGroupCategory(scene.sceneGroup!),
        sceneDesignId: scene.sceneDesign,
    });

    return asset;
}

export const addStickerAssetToSceneInternalOperation = createLuminaryOperation(
    addStickerAssetToSceneInternal,
);

export const configureStickerAssetOperation = createLuminaryOperation(
    (
        { luminary, call },
        assetId: string,
        sceneId: string,
        propertyOverrides?: Partial<ConfigurableElementProperties>,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            return;
        }
        if (!VideoData.isStickerAsset(asset)) {
            throw new Error('Provided asset is not a sticker.');
        }
        if (!VideoData.isMediaAsset(asset) && !VideoData.isIconAsset(asset)) {
            throw new Error('Provided asset is not a media or icon asset.');
        }

        luminary.batchRun((safeLuminary) => {
            asset.transformation.anchor.x = 0.5;
            asset.transformation.anchor.y = 0.5;

            if (!propertyOverrides) {
                return;
            }
            const { position, dimensions, introOutroTimes, dynamicStyles, groupIndex } =
                propertyOverrides;
            if (typeof groupIndex === 'number') {
                const parent = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
                parent.moveAsset(asset, groupIndex);
            }

            if (dimensions) {
                const assetAspectRatio = dimensions.width / dimensions.height;
                let targetWidth = dimensions.width;
                let targetHeight = dimensions.height;
                if (targetWidth > safeLuminary.referenceResolution.width) {
                    targetWidth = safeLuminary.referenceResolution.width;
                    targetHeight = targetWidth / assetAspectRatio;
                }
                if (targetHeight > safeLuminary.referenceResolution.height) {
                    targetHeight = safeLuminary.referenceResolution.height;
                    targetWidth = targetHeight * assetAspectRatio;
                }

                asset.maxWidth = targetWidth;
                asset.maxHeight = targetHeight;
            }

            if (position) {
                call(
                    setAssetPositionTransformationOperation,
                    {
                        sceneId,
                        assetId,
                        ...position,
                    },
                    { luminary: safeLuminary },
                );
            } else {
                // If no target position is provided, center the sticker on the canvas aka click on sticker without draging onto scene
                const centerX = safeLuminary.referenceResolution.width / 2;
                const centerY = safeLuminary.referenceResolution.height / 2;
                asset.setTransformationPositionWithinSceneBounds(centerX, centerY);
            }

            if (introOutroTimes) {
                call(
                    setAssetIntroExitTimesOperation,
                    {
                        timingSpecifications: [
                            {
                                sceneId,
                                assetId,
                                ...introOutroTimes,
                            },
                        ],
                        operatingOnMainLuminary: true,
                        systemTriggered,
                        parentActionGroup,
                    },
                    { luminary: safeLuminary },
                );
            }

            if (dynamicStyles) {
                try {
                    // remove existing setup
                    asset.destroyDynamicStyles();
                    asset.loadDynamicStyle(dynamicStyles);
                    // on media assets, color graphics are on a seperate container so we have to
                    // manually apply object animations
                    if (VideoData.isMediaAsset(asset)) {
                        const targetColorGraphics = asset.colorGraphics;

                        targetColorGraphics.forEach((colorGraphic) => {
                            dynamicStyles.objectAnimation.forEach((objectAnimation) => {
                                const animation = colorGraphic.addAnimation();
                                animation.load(objectAnimation, {});
                            });
                        });
                    }
                } catch (e) {
                    ErrorReporter.capture(e);
                }
            }
        });
    },
);

export const deleteAssetOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, assetId: string) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        const asset = scene.getAssetById(assetId);
        if (!asset) {
            return;
        }

        const isSticker = VideoData.isStickerAsset(asset);

        const isChartAsset = VideoData.isChartAsset(asset);
        if (!isSticker && !VideoData.isIconAsset(asset) && !isChartAsset) {
            ErrorReporter.capture(`${asset.classType} cannot be deleted.`);
            return;
        }

        const assetType = asset.classType;
        const isWaveform =
            VideoData.isIconAsset(asset) && asset.playbackMode === VideoData.PLAYBACK_WAVEFORM;

        luminary.batchRun(() => {
            if (VideoData.isAssetGroup(asset)) {
                const deleteChildrenRecursively = (assetGroup: AssetGroup) => {
                    assetGroup.getAssets({ deep: false }).forEach((child) => {
                        if (VideoData.isAssetGroup(child)) {
                            deleteChildrenRecursively(child);
                        }
                        assetGroup.removeChild(child);
                        child.destroy();
                    });
                };
                deleteChildrenRecursively(asset);
            }

            const parent = asset.getParent();
            if (parent) {
                parent.removeChild(asset);
            }
            asset.destroy();
            scene.stickers.requiresRedraw = true;
        });

        analytics.track('Asset deleted from scene', {
            type: assetType,
            is_waveform: isWaveform,
        });
    },
);
